import {
    Alert,
    Grid,
    Link,
    Stack,
    StackItem,
    Tooltip,
    Typography,
} from "@octopus-energy/coral/atoms"
import { FormikTextField } from "@octopus-energy/coral/formik"
import { IconCircleInformationOutlined } from "@octopus-energy/icons"
import { useIsOpen } from "@oefr/hooks"
import { useTranslations } from "next-intl"
import { AddressField } from "@/components/molecules"
import { MeterPointAccordion } from "@/features/meter-points/components/MeterPointAccordion"
import { EnergyType } from "@/features/subscription"
import { useInitialOnboardingFormContext } from "../../context/InitialOnboardingFormContext"
import { GasMeterSerialAccordion } from "../GasMeterSerialAccordion"

type MeterPointIdFormProps = {
    energyType: EnergyType
    isSerialMode?: boolean
    onSwitchToSerial?: () => void
    onSwitchToPce?: () => void
    serialPceNotFound?: boolean
}

export function MeterPointIdForm({
    energyType,
    isSerialMode = false,
    onSwitchToSerial,
    onSwitchToPce,
    serialPceNotFound = false,
}: MeterPointIdFormProps) {
    const t = useTranslations("MeterPointIdForm")

    const { journeyType, onboardingState, getGasMeterPoint, getElectricityMeterPoint } =
        useInitialOnboardingFormContext()

    const { propertyAddress, subscriptionType } = onboardingState.formValues

    const meterPoint =
        energyType === EnergyType.Gas ? getGasMeterPoint() : getElectricityMeterPoint()

    const displayChangeOfSupplierError =
        journeyType === "CHANGE_OF_SUPPLIER" && meterPoint?.hasChangeOfSupplierError

    const isGas = energyType === EnergyType.Gas

    const { isOpen: isCustomAddressFormOpen, open: openCustomAddressForm } = useIsOpen()

    if (isGas && isSerialMode) {
        return (
            <>
                <Stack>
                    <Typography textStyle="h4">
                        {t(`${energyType}.title`, {
                            propertyAddress: !!propertyAddress,
                        })}
                    </Typography>
                </Stack>
                {isCustomAddressFormOpen ? (
                    <>
                        <FormikTextField
                            label={t("gas.serial.address.streetLabel")}
                            name="gas.address.addrNumStreet"
                            autocomplete="address-line1"
                        />
                        <Stack flexDirection={{ base: "column", md: "row" }}>
                            <StackItem flex="1">
                                <FormikTextField
                                    label={t("gas.serial.address.postcodeLabel")}
                                    name="gas.address.addrPostCode"
                                    autocomplete="postal-code"
                                />
                            </StackItem>
                            <StackItem flex="2">
                                <FormikTextField
                                    label={t("gas.serial.address.cityLabel")}
                                    name="gas.address.addrCityLabel"
                                    autocomplete="address-level2"
                                />
                            </StackItem>
                        </Stack>
                    </>
                ) : (
                    <AddressField
                        label={t("gas.serial.address.label")}
                        fieldName="gas.address"
                        onOptionSelect={openCustomAddressForm}
                    />
                )}

                {!isCustomAddressFormOpen && (
                    <Link onClick={openCustomAddressForm}>
                        {t("gas.serial.address.notFoundable")}
                    </Link>
                )}

                <Grid
                    gap="md"
                    gridTemplateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
                >
                    <FormikTextField
                        attributes={{ "data-testid": "givenName" }}
                        label={t("user.firstName")}
                        name="gas.givenName"
                        autocomplete="given-name"
                        disabled={subscriptionType === "MOVE_OUT"}
                    />
                    <FormikTextField
                        attributes={{ "data-testid": "familyName" }}
                        label={t("user.lastName")}
                        name="gas.familyName"
                        autocomplete="family-name"
                        disabled={subscriptionType === "MOVE_OUT"}
                    />
                </Grid>

                <FormikTextField
                    attributes={{ "data-testid": "gasMeterSerialNumber" }}
                    label={t("gas.serial.serialNumber.label")}
                    name="gas.meterSerialNumber"
                />

                <GasMeterSerialAccordion />
                <Link onClick={onSwitchToPce}>{t("gas.pceKnownLink")}</Link>
            </>
        )
    }

    return (
        <>
            <Stack>
                <Typography textStyle="h4">
                    {t(`${energyType}.title`, {
                        propertyAddress: !!propertyAddress,
                    })}
                </Typography>
                {propertyAddress && (
                    <Tooltip title={t("common.tooltip")}>
                        <IconCircleInformationOutlined size={24} />
                    </Tooltip>
                )}
            </Stack>

            {isGas && serialPceNotFound && (
                <Alert severity="info">
                    <Typography color="inherit">{t("gas.serial.pceNotFound")}</Typography>
                </Alert>
            )}

            <FormikTextField
                name={`${energyType}.meterPointId`}
                label={t(`${energyType}.input.label`)}
                inputProps={{ inputMode: "numeric" }}
                attributes={{ "data-testid": `${energyType}MeterPointInput` }}
            />

            <Grid
                gap="md"
                gridTemplateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
                attributes={{ "data-testid": `${energyType}UserInfos` }}
            >
                <FormikTextField
                    attributes={{ "data-testid": "givenName" }}
                    label={t("user.firstName")}
                    name={`${energyType}.givenName`}
                    autocomplete="given-name"
                />
                <FormikTextField
                    attributes={{ "data-testid": "familyName" }}
                    label={t("user.lastName")}
                    name={`${energyType}.familyName`}
                    autocomplete="family-name"
                />
            </Grid>

            <MeterPointAccordion energyType={energyType} />

            {isGas && onSwitchToSerial && (
                <Link onClick={onSwitchToSerial}>{t("gas.serialLookupLink")}</Link>
            )}

            {displayChangeOfSupplierError && (
                <Stack flexDirection="column">
                    <Alert severity="info">
                        <Typography color="inherit">
                            {t("common.changeOfSupplierError", { energyType })}
                        </Typography>
                    </Alert>
                </Stack>
            )}
        </>
    )
}

MeterPointIdForm.messages = [
    "MeterPointIdForm",
    ...MeterPointAccordion.messages,
    ...GasMeterSerialAccordion.messages,
    ...AddressField.messages,
] as const
